const DEFAULT_SECTION_COUNTS = 32;
const MAX_SECTION_COUNTS = 512;
const RECOGNISED_32_BAR_FORMS = new Set(['AABA', 'ABAB', 'ABAC']);
// MCRO001-F0064 (T-MCRO-064): bounded stage of one @formation line.
const FORMATION_MAX_PEOPLE = 32;

const stripBullet = (value) => String(value || '').replace(/^\s*[-*#•]+\s*/, '').trim();
const titleCase = (value) => `${value[0].toUpperCase()}${value.slice(1).toLowerCase()}`;

function numberedHeading(text) {
  const match = text.match(/^(phrase|section)\s*(\d+)(?:\s*[-–—:]\s*(.*))?$/i);
  if (!match) return null;
  const kind = match[1].toLowerCase();
  const number = Number(match[2]);
  const detail = String(match[3] || '').trim();
  const displayDetail = /^melody\s+/i.test(detail)
    ? detail.replace(/^melody\s+/i, 'Melody ')
    : detail;
  return {
    type: 'numbered',
    kind,
    number,
    label: `${titleCase(kind)} ${number}${displayDetail ? ` · ${displayDetail}` : ''}`,
  };
}

function explicitCountHeading(text) {
  // Separator-free shorthand requires a label before a spaced `x8`. This
  // accepts "Intro x8" without treating repetitions such as "3x Box Steps"
  // as structural headings.
  const multiplied = text.match(/^(.+?)\s*[-–—:]\s*(\d+)\s*[x×]\s*8(?:\s*counts?)?\s*$/i);
  const direct = text.match(/^(.+?)\s*[-–—:]\s*(\d+)\s*(?:counts?|cts?)\s*$/i);
  const shorthand = text.match(/^(.+?[a-z])\s+[x×]\s*8(?:\s*counts?)?\s*$/i);
  const match = multiplied || direct || shorthand;
  if (!match || !/[a-z]/i.test(match[1])) return null;
  const countExpression = shorthand ? '8' : match[2];
  const countLength = multiplied ? Number(match[2]) * 8 : Number(countExpression);
  if (!Number.isSafeInteger(countLength) || countLength < 1 || countLength > MAX_SECTION_COUNTS) {
    return {
      type: 'invalid-explicit-count',
      label: match[1].trim(),
      rejectedCountExpression: multiplied ? `${match[2]} x 8` : countExpression,
    };
  }
  return {
    type: 'explicit-count',
    label: match[1].trim(),
    countLength,
  };
}

function namedHeading(text) {
  if (text.length > 80) return null;
  const match = text.match(/^([^.!?]+):$/);
  if (!match || !/[a-z]/i.test(match[1])) return null;
  return { type: 'named', label: match[1].trim() };
}

function symbolicHeading(text) {
  const section = text.match(/^section\s+([A-Z])\s*:?$/i);
  if (section) return { type: 'named', label: `Section ${section[1].toUpperCase()}` };
  const letter = text.match(/^([A-Z])\s*:?$/);
  if (letter) return { type: 'named', label: `Section ${letter[1]}` };
  if (/^(intro|verse|pre-chorus|chorus|bridge|break|outro)$/i.test(text)) {
    return { type: 'named', label: titleCase(text) };
  }
  return null;
}

function headingFor(text) {
  return numberedHeading(text) || explicitCountHeading(text) || symbolicHeading(text) || namedHeading(text);
}

function explicitBarFor(text) {
  const shorthand = text.match(/^\[(\d+)\s*[bc]\](?:\s+(.*))?$/i);
  const countPhrase = text.match(/^\[(\d+)\s*(?:counts?|cts?)\s+here\](?:\s+(.*))?$/i);
  const canvasAllocation = text.match(/^\[(?:(\d+)\s*[x×]\s*)?(8)\](?:\s+(.*))?$/i);
  const blue = text.match(/^blue\s+(\d+)\s*$/i);
  const match = shorthand || countPhrase || canvasAllocation || blue;
  if (!match) return null;
  const countLength = canvasAllocation ? Number(canvasAllocation[2]) : Number(match[1]);
  if (!Number.isSafeInteger(countLength) || countLength < 1 || countLength > 64) return null;
  const movement = String(canvasAllocation ? canvasAllocation[3] : match[2] || '').replace(/\s*\/\/.*$/, '').trim();
  const repeatedTemplate = movement.match(/^(\d+)\s*x\s*\[[^\]]+\]\s*$/i);
  const repeat = canvasAllocation && canvasAllocation[1]
    ? Number(canvasAllocation[1])
    : repeatedTemplate ? Number(repeatedTemplate[1]) : 1;
  if (!Number.isSafeInteger(repeat) || repeat < 1 || repeat > 64) return null;
  return {
    countLength,
    repeat,
    label: countLength === 6 && (countPhrase || blue) ? 'Blue 6' : undefined,
  };
}

function normaliseLines(notes) {
  return String(notes || '').split(/\r?\n/).map((raw, index) => {
    const bareSection = raw.trim() === '#';
    const markdownHeading = raw.trim().match(/^#(?!#)\s*(.+)$/);
    const writtenHeading = markdownHeading?.[1]?.trim();
    const text = bareSection
      ? '#'
      : writtenHeading
      ? (/^[a-z]$/i.test(writtenHeading) ? `Section ${writtenHeading.toUpperCase()}` : writtenHeading)
      : stripBullet(raw);
    return { raw, line: index + 1, text, bareSection };
  }).filter(({ text }) => text.length > 0);
}

function canvasAllocationFor(raw) {
  // MCRO001-F0062 (T-MCRO-062): this live projection must accept exactly the
  // allocation tokens the authoritative F0012 checkpoint parser accepts
  // (studioaeye-choreo backend/src/services/choreographyCanvasParser.ts):
  // `[8]`, `[N×8]`, a bare `[N]` meaning N counts with ceil(N/8) rows, a
  // tolerated trailing `b`/`c` with no semantic effect, bounds 1–1024 counts.
  // A stricter grammar here silently diverges from the snapshot the same
  // source produces on save: the mirror loses its data-count anchors for the
  // unaccepted tokens and MCRO001-F0028 rail geometry falls back to an even
  // division of the mirror box — rails drawn detached from their bars.
  const match = String(raw || '').trim().match(/^\[(?:(\d+)\s*[x×]\s*8|(\d+))(?:b|c)?\]\s*(.*)$/i);
  if (!match) return null;
  const countLength = match[1] ? Number(match[1]) * 8 : Number(match[2]);
  if (!Number.isSafeInteger(countLength) || countLength < 1 || countLength > 1024) return null;
  const rowSpan = Math.ceil(countLength / 8);
  const durationLabel = match[1]
    ? `[${Number(match[1])}×8]`
    : countLength > 8 && countLength % 8 === 0 ? `[${countLength / 8}×8]` : `[${countLength}]`;
  return { rowSpan, countLength, durationLabel, movement: match[3].trim() };
}

// Display rows are eight-count slices relative to a section boundary. A block's
// snapshot spanRows remains ceil(countLength / 8), but a block that starts part
// way through a display row may touch more rows than that metadata says.
export function allocationFragmentsFor(countStart, countLength, sectionStartCount) {
  const fragments = [];
  const countEnd = countStart + countLength - 1;
  let fragmentStart = countStart;
  while (fragmentStart <= countEnd) {
    const row = Math.floor((fragmentStart - sectionStartCount) / 8) + 1;
    const rowStartCount = sectionStartCount + (row - 1) * 8;
    const fragmentEnd = Math.min(countEnd, rowStartCount + 7);
    fragments.push({
      row,
      countStart: fragmentStart,
      countEnd: fragmentEnd,
      startCell: fragmentStart - rowStartCount + 1,
      endCell: fragmentEnd - rowStartCount + 1,
    });
    fragmentStart = fragmentEnd + 1;
  }
  return fragments;
}

// MCRO001-F0064 (T-MCRO-064): one `@formation <count>: …` line anchors a
// numbered-dancer formation to an absolute count. This live projection must
// make the SAME accept/reject decisions as the authoritative F0012 checkpoint
// parser (studioaeye-choreo backend/src/services/choreographyCanvasParser.ts,
// design authority MCRO001-D000 §"Numbered dancer positions"), exactly as the
// F0062 allocation comment below requires for `[n×8]`: a stricter or looser
// grammar here would silently diverge from the snapshot the same source
// produces on save.
//
// Grammar: entries are whitespace-separated, each `number:x,y` or
// `number=Label:x,y` (label display-only, 1–40 characters, no whitespace or
// colon). `number` is 1–99 and unique within one line; `x`/`y` are integers
// 0–100 (x audience left→right, y upstage 0 → downstage 100); at most 32
// entries per line; `count` is an integer ≥ 1.
//
// Malformed-line convention: a line that begins `@formation` but fails ANY of
// these checks (or repeats an already-accepted count in the section, or names
// a count outside the section's absolute span — enforced at section close, the
// only point where the span is known) is REJECTED as a formation: it is never
// half-applied, never advances the section's accepted-count sequence, and the
// line falls through to this projection's ordinary unrecognised-line handling
// (a visible provisional row). Rejected-into-visible mirrors both F0012's
// skip-invalid-token decision and this projection's never-silently-discard
// rule for authored text.
function canvasFormationFor(raw) {
  const match = String(raw || '').trim().match(/^@formation\s+(\d+)\s*:\s*(\S.*)$/i);
  if (!match) return null;
  const count = Number(match[1]);
  if (!Number.isSafeInteger(count) || count < 1) return null;
  const seenNumbers = new Set();
  const positions = [];
  const entries = match[2].trim().split(/\s+/);
  if (entries.length > FORMATION_MAX_PEOPLE) return null;
  for (const entry of entries) {
    const parsed = entry.match(/^(\d{1,2})(?:=([^:\s]+))?:(\d+),(\d+)$/);
    if (!parsed) return null;
    const number = Number(parsed[1]);
    const x = Number(parsed[3]);
    const y = Number(parsed[4]);
    if (number < 1 || number > 99 || seenNumbers.has(number)) return null;
    if (!Number.isSafeInteger(x) || x < 0 || x > 100) return null;
    if (!Number.isSafeInteger(y) || y < 0 || y > 100) return null;
    const label = parsed[2];
    if (label !== undefined && label.length > 40) return null;
    seenNumbers.add(number);
    positions.push({ number, ...(label !== undefined ? { label } : {}), x, y });
  }
  return { count, positions };
}

// MCRO001-F0065 (T-MCRO-065): `@group <name> = <numbers>` binds a named,
// possibly-overlapping group of cast numbers (the F0064 number grammar:
// 1–99). The name is 1–20 characters matching [A-Za-z][A-Za-z0-9_-]*, the
// list holds at least one number, and numbers are unique WITHIN the line
// (overlap ACROSS groups is the point). Like canvasFormationFor above, this
// must make the same accept/reject decisions as the authoritative F0012
// parser: a line that begins `@group` but fails any check is REJECTED as a
// group — never half-applied — and falls through to the ordinary
// unrecognised-line handling (a visible provisional row inside a section).
function canvasGroupFor(raw) {
  const match = String(raw || '').trim().match(/^@group\s+([A-Za-z][A-Za-z0-9_-]{0,19})\s*=\s*(\S.*)$/i);
  if (!match) return null;
  const seen = new Set();
  const numbers = [];
  for (const piece of match[2].split(/\s*,\s*/)) {
    if (!/^\d{1,2}$/.test(piece.trim())) return null;
    const number = Number(piece.trim());
    if (number < 1 || number > 99 || seen.has(number)) return null;
    seen.add(number);
    numbers.push(number);
  }
  if (numbers.length === 0) return null;
  return { name: match[1], numbers };
}

// MCRO001-F0065 (T-MCRO-065): `@variant <name>` opens a variation block for
// the named group. The block's ordinary allocation lines REPLACE the base
// allocations the block names (the trailing contiguous base run) for that
// group's dancers only; the block ends when its lines have covered the named
// span (the next base line then applies to everyone again) or the next
// `@variant`/section boundary arrives. Rejected lines fall through exactly
// like rejected `@group`/`@formation` lines.
function canvasVariantFor(raw) {
  const match = String(raw || '').trim().match(/^@variant\s+([A-Za-z][A-Za-z0-9_-]{0,19})$/i);
  if (!match) return null;
  return { name: match[1] };
}

// Shared normalisation for the pure formation projections: anchors sorted by
// count, each number's own appearance series in count order. A number only
// ever moves between two anchors that BOTH name it — absence means
// last-known-wins (T-MCRO-064 case D).
function formationSeriesFor(formations) {
  const anchors = (Array.isArray(formations) ? formations : [])
    .map((formation) => ({ count: Number(formation?.count), positions: Array.isArray(formation?.positions) ? formation.positions : [] }))
    .filter(({ count }) => Number.isSafeInteger(count) && count >= 1)
    .sort((a, b) => a.count - b.count);
  const series = new Map();
  anchors.forEach(({ count, positions }) => {
    positions.forEach((position) => {
      const number = Number(position?.number);
      const x = Number(position?.x);
      const y = Number(position?.y);
      if (!Number.isSafeInteger(number) || number < 1 || !Number.isFinite(x) || !Number.isFinite(y)) return;
      if (!series.has(number)) series.set(number, []);
      series.get(number).push({ count, x, y, ...(position?.label !== undefined ? { label: String(position.label) } : {}) });
    });
  });
  return series;
}

/**
 * MCRO001-F0064 (T-MCRO-064): the interpolated numbered-dancer positions at an
 * absolute count. For each number ever named in the section's formations this
 * returns `{ number, x, y, label? }` — the linear interpolation between the
 * two surrounding anchors of that number's OWN series, held before its first
 * and after its last appearance (last-known-wins for numbers a later anchor
 * does not name). Coordinates are exact floats; rendering maps them straight
 * to percentages of the stage rectangle.
 */
export function formationAtCount(formations, count) {
  const series = formationSeriesFor(formations);
  const at = Math.max(1, Number(count) || 1);
  const positions = [];
  [...series.keys()].sort((a, b) => a - b).forEach((number) => {
    const appearances = series.get(number);
    const first = appearances[0];
    if (at <= first.count) {
      positions.push({ number, x: first.x, y: first.y, ...(first.label !== undefined ? { label: first.label } : {}) });
      return;
    }
    const previous = [...appearances].reverse().find(({ count: c }) => c <= at);
    const next = appearances.find(({ count: c }) => c > at) || null;
    const progress = next ? (at - previous.count) / (next.count - previous.count) : 0;
    const deltaX = next ? (next.x - previous.x) * progress : 0;
    const deltaY = next ? (next.y - previous.y) * progress : 0;
    positions.push({
      number,
      x: previous.x + deltaX,
      y: previous.y + deltaY,
      ...(previous.label !== undefined ? { label: previous.label } : next?.label !== undefined ? { label: next.label } : {}),
    });
  });
  return positions;
}

/**
 * MCRO001-F0064 (T-MCRO-064): each number's straight-line movement path
 * between its consecutive anchors — `{ number, from: {x, y}, to: {x, y} }`.
 * A number absent from an anchor generates no path across it (it holds).
 */
export function formationPaths(formations) {
  const series = formationSeriesFor(formations);
  const paths = [];
  [...series.keys()].sort((a, b) => a - b).forEach((number) => {
    const appearances = series.get(number);
    for (let index = 1; index < appearances.length; index += 1) {
      paths.push({
        number,
        from: { x: appearances[index - 1].x, y: appearances[index - 1].y },
        to: { x: appearances[index].x, y: appearances[index].y },
      });
    }
  });
  return paths;
}

// MCRO001-F0065 (T-MCRO-065): the per-row OWNER model underneath the
// per-dancer projection and the coverage report. For one cast number, walk
// every allocation in document order and decide who owns each layout row:
//
// - a base allocation owns its own rows (later base writes overwrite, the
//   deterministic document-order rule);
// - a variant allocation of one of the dancer's groups FIRST voids the whole
//   span its block replaced (replaced-but-never-covered rows are exactly the
//   coverage GAP — the dancer would stand still with nothing to do), then
//   writes its own covered rows (effectiveRows, the inherited slice it
//   consumed). A later block therefore wins on overlap purely by document
//   order (D015 §3.4's later-block-wins), and writing over an earlier
//   DIFFERENT group's variant rows is the AMBIGUITY the validator warns on.
// - a variant allocation of a group the dancer is NOT in is skipped entirely.
//
// Rows are the atomic unit: canvas allocation spans are whole eight-count
// rows, so count-level overlaps cannot be finer than a row. Absolute counts
// accumulate exactly as canvasDocumentLines anchors them (sectionStartCount
// plus rows × 8 per section).
function variantTimeline(score, castNumber) {
  const sections = score?.sections || [];
  const number = Math.floor(Number(castNumber) || 0);
  const memberOf = new Set((score?.groups || [])
    .filter((group) => (group.numbers || []).includes(number))
    .map((group) => group.name));
  const owners = new Map();
  const ambiguityRows = [];
  sections.forEach((section, sectionIndex) => {
    (section.allocations || []).forEach((allocation) => {
      if (allocation.variant !== undefined) {
        if (!memberOf.has(allocation.variant)) return;
        // Overlap is judged against ownership BEFORE this block voids the
        // replaced span: a later block covering rows an earlier group's block
        // already covered is exactly the ambiguity (D015 §3.4), even though
        // the later block's replace-and-cover wins the rows.
        const priorCoverage = new Map();
        for (let row = Number(allocation.effectiveRows.startRow); row <= Number(allocation.effectiveRows.endRow); row += 1) {
          priorCoverage.set(row, owners.get(`${sectionIndex}:${row}`));
        }
        for (let row = Number(allocation.replacedRows.startRow); row <= Number(allocation.replacedRows.endRow); row += 1) {
          const previous = owners.get(`${sectionIndex}:${row}`);
          // A block must not void its OWN group's earlier coverage: the lines
          // of one block compose a single replacement. A DIFFERENT group's
          // coverage is voided here — later-block-wins — before this block
          // writes its own rows.
          if (previous?.type === 'variant' && previous.name === allocation.variant) continue;
          owners.set(`${sectionIndex}:${row}`, { type: 'void' });
        }
        for (let row = Number(allocation.effectiveRows.startRow); row <= Number(allocation.effectiveRows.endRow); row += 1) {
          const previous = priorCoverage.get(row);
          if (previous?.type === 'variant' && previous.name !== allocation.variant) {
            ambiguityRows.push({ sectionIndex, row, groups: [previous.name, allocation.variant] });
          }
          owners.set(`${sectionIndex}:${row}`, { type: 'variant', allocation, name: allocation.variant });
        }
        return;
      }
      for (let row = Number(allocation.startRow); row <= Number(allocation.endRow); row += 1) {
        owners.set(`${sectionIndex}:${row}`, { type: 'base', allocation });
      }
    });
  });
  // Merge adjacent same-kind rows into count runs. A row's absolute counts
  // need the section's start count, so runs are collected per section.
  const gaps = [];
  const ambiguities = [];
  const sectionLength = (section, startCount) => {
    if (Number.isInteger(section.endCount)) return Math.max(0, section.endCount - startCount + 1);
    if (Number.isInteger(section.countEnd)) return Math.max(0, section.countEnd - startCount + 1);
    if (Number.isSafeInteger(section.allocatedCountLength)) return Math.max(0, section.allocatedCountLength);
    return Number(section.rows || 0) * 8; // legacy score shape only
  };
  let nextCount = 1;
  sections.forEach((section, sectionIndex) => {
    const rowCount = Number(section.rows || 0);
    const sectionStartCount = Number.isInteger(section.countStart) ? section.countStart : nextCount;
    const sectionEndCount = sectionStartCount + sectionLength(section, sectionStartCount) - 1;
    const countOf = (row) => sectionStartCount + (row - 1) * 8;
    let gapRun = null;
    for (let row = 1; row <= rowCount; row += 1) {
      const isVoid = owners.get(`${sectionIndex}:${row}`)?.type === 'void';
      if (isVoid && gapRun) gapRun.to = row;
      else if (isVoid && countOf(row) <= sectionEndCount) gapRun = { from: row, to: row };
      else if (gapRun) { gaps.push({ number, fromCount: countOf(gapRun.from), toCount: Math.min(sectionEndCount, countOf(gapRun.to) + 7) }); gapRun = null; }
    }
    if (gapRun) gaps.push({ number, fromCount: countOf(gapRun.from), toCount: Math.min(sectionEndCount, countOf(gapRun.to) + 7) });
    nextCount = sectionEndCount + 1;
  });
  ambiguityRows
    .sort((a, b) => a.sectionIndex - b.sectionIndex || a.row - b.row)
    .forEach(({ sectionIndex, row, groups }) => {
      const previous = ambiguities[ambiguities.length - 1];
      const sectionStart = Number.isInteger(sections[sectionIndex]?.countStart)
        ? sections[sectionIndex].countStart
        : sections.slice(0, sectionIndex).reduce((total, section) => total + sectionLength(section, total), 1);
      const fromCount = sectionStart + (row - 1) * 8;
      const sectionEndCount = sectionStart + sectionLength(sections[sectionIndex], sectionStart) - 1;
      if (previous && previous.groups[0] === groups[0] && previous.groups[1] === groups[1] && previous.toCount === fromCount - 1) {
        previous.toCount = Math.min(sectionEndCount, fromCount + 7);
        return;
      }
      ambiguities.push({ number, fromCount, toCount: Math.min(sectionEndCount, fromCount + 7), groups: [...groups] });
    });
  return { memberOf, owners, gaps, ambiguities };
}

/**
 * MCRO001-F0065 (T-MCRO-065): the per-dancer view of the one Sheet — base
 * allocations minus the spans a variant of the dancer's groups replaced, plus
 * those variants' lines (later block wins on overlap; D015 §3.3). Returns the
 * score shape with each section's allocations filtered to the dancer's
 * EFFECTIVE ones, each carrying `effectiveRows` (the rows it actually answers
 * for in this view). A dancer in no group simply receives the base.
 */
export function effectiveChoreography(score, castNumber) {
  const sections = score?.sections || [];
  const { memberOf, owners } = variantTimeline(score, castNumber);
  const outSections = sections.map((section, sectionIndex) => {
    const surviving = new Map();
    (section.allocations || []).forEach((allocation) => {
      const span = allocation.effectiveRows || allocation;
      const rows = [];
      for (let row = Number(span.startRow); row <= Number(span.endRow); row += 1) {
        if (owners.get(`${sectionIndex}:${row}`)?.allocation === allocation) rows.push(row);
      }
      if (rows.length > 0) surviving.set(allocation, { startRow: rows[0], endRow: rows[rows.length - 1] });
    });
    return {
      ...section,
      allocations: (section.allocations || [])
        .filter((allocation) => surviving.has(allocation))
        .map((allocation) => ({ ...allocation, effectiveRows: surviving.get(allocation) })),
    };
  });
  return { castNumber: Math.floor(Number(castNumber) || 0), groups: [...memberOf], sections: outSections };
}

/**
 * MCRO001-F0065 (T-MCRO-065): the variation-specific coverage warnings for
 * the validator (D015 §3.4). `gaps` are replaced-but-never-covered count runs
 * per dancer (they would stand still with nothing to do); `ambiguities` are
 * same-dancer same-count overlaps between two of that dancer's groups, with
 * the deterministic later-block-wins resolution named for the warning.
 */
export function coverageReport(score) {
  const numbers = [...new Set((score?.groups || []).flatMap((group) => group.numbers || []))]
    .filter((value) => Number.isSafeInteger(value) && value >= 1)
    .sort((a, b) => a - b);
  const gaps = [];
  const ambiguities = [];
  numbers.forEach((number) => {
    const timeline = variantTimeline(score, number);
    gaps.push(...timeline.gaps);
    ambiguities.push(...timeline.ambiguities);
  });
  return { gaps, ambiguities };
}

/**
 * MCRO001-F0065 (T-MCRO-065): the mirror-level view map for a cast number —
 * `{ hidden: [sourceLine], variants: { [sourceLine]: groupName } }`. The
 * full-team view (no cast number) hides nothing but still names every
 * group-carrying line so the mirror can colour-tag it. Per dancer, the
 * replaced base allocations and the OTHER groups' variant blocks (including
 * their @variant tokens) are hidden; @group definition lines are sheet-level
 * and always stay visible.
 */
export function variantLineView(score, castNumber) {
  const raw = String(castNumber ?? '');
  const number = Number(raw);
  const viewing = raw !== '' && Number.isSafeInteger(number) && number >= 1;
  const memberOf = viewing
    ? new Set((score?.groups || []).filter((group) => (group.numbers || []).includes(number)).map((group) => group.name))
    : null;
  const effective = viewing ? effectiveChoreography(score, number) : null;
  const surviving = new Set();
  effective?.sections.forEach((section) => section.allocations.forEach((allocation) => surviving.add(allocation.id)));
  const hidden = [];
  const variants = {};
  const hideAllocation = (allocation) => {
    const lines = allocation.movementLines.length > 0
      ? allocation.movementLines.map(({ sourceLine }) => sourceLine)
      : [allocation.sourceLine];
    if (viewing && !surviving.has(allocation.id)) hidden.push(...lines);
    else if (allocation.variant !== undefined) lines.forEach((sourceLine) => { variants[sourceLine] = allocation.variant; });
  };
  (score?.sections || []).forEach((section) => (section.allocations || []).forEach(hideAllocation));
  (score?.groupTokenLines || []).forEach((token) => {
    if (token.kind === 'group') { variants[token.sourceLine] = token.name; return; }
    if (!viewing || memberOf.has(token.name)) variants[token.sourceLine] = token.name;
    else hidden.push(token.sourceLine);
  });
  return { hidden, variants };
}

/**
 * Project the deliberately small Canvas grammar into visual row ownership. This is
 * separate from musical inference: it only formats explicit # / ## / [n×8] source.
 */
export function projectChoreoCanvas(notes) {
  const sections = [];
  let section = null;
  let sequence = null;
  let allocation = null;
  // MCRO001-F0064 (T-MCRO-064): formations captured inside the open section,
  // validated against the section's absolute count span at section close —
  // the only point where that span (startCount plus rows × 8, the same count
  // model canvasDocumentLines anchors data-count-start/end with) is known.
  let pendingFormations = [];
  let sectionStartCount = 1;
  // MCRO001-F0065 (T-MCRO-065): sheet-level group definitions and the source
  // lines of accepted @group/@variant tokens (the mirror tints those lines).
  // Groups are SHEET-level (they bind cast numbers, not counts), so they are
  // accepted with or without an open section; @variant blocks are per-section
  // (they replace base allocations) and are rejected outside one.
  const groups = [];
  const groupNames = new Set();
  const groupTokenLines = [];
  // Per-section variation state: `variantState` is the open block — the
  // remaining inherited rows (each tagged with the base allocation it came
  // from) and the named run they came from. `baseRun` is the trailing
  // contiguous base run since the last block closed; `releasedRun` is the
  // last closed block's named run, so CONSECUTIVE `@variant` blocks (A then
  // B over the same base span, the D015 §3.2 example) each inherit the SAME
  // preceding base run until a fresh base allocation resumes after them.
  let variantState = null;
  let baseRun = [];
  let releasedRun = null;
  // Consume `rowSpan` rows off the open block's inherited queue for a new
  // variant-marked allocation, or extend the trailing base run for a base
  // allocation. Returns the marking spread for the allocation (empty for
  // base). The block CLOSES the moment its queue empties — the next base
  // allocation applies to everyone again (D015 §3.2).
  const variantMarkFor = (startRow, rowSpan) => {
    if (!variantState) {
      baseRun.push({ startRow, endRow: startRow + rowSpan - 1 });
      return {};
    }
    const name = variantState.name;
    const consumed = variantState.queue.slice(0, rowSpan);
    variantState.queue = variantState.queue.slice(consumed.length);
    const marking = {
      variant: name,
      effectiveRows: { startRow: consumed[0].row, endRow: consumed[consumed.length - 1].row },
      replacedRows: {
        startRow: variantState.named[0].startRow,
        endRow: variantState.named[variantState.named.length - 1].endRow,
      },
    };
    if (variantState.queue.length === 0) {
      releasedRun = variantState.named;
      baseRun = [];
      variantState = null;
    }
    return marking;
  };

  const finishAllocation = () => { allocation = null; };
  const finishSection = () => {
    if (!section) return;
    // One untimed movement line beneath a section heading is the section's
    // complete movement, not an isolated first bar. Keep the inference marked
    // provisional while giving it the full four-bar visual allocation.
    if (section.allocations.length === 1 && section.allocations[0].provisional) {
      const [onlyAllocation] = section.allocations;
      onlyAllocation.rowSpan = 4;
      onlyAllocation.countLength = 32;
      onlyAllocation.durationLabel = '[4×8]';
    }
    section.countStart = sectionStartCount;
    section.allocatedCountLength = section.allocations.reduce((total, item) => total + item.countLength, 0);
    let nextAllocationCount = sectionStartCount;
    section.allocations.forEach((item) => {
      item.countStart = nextAllocationCount;
      item.countEnd = nextAllocationCount + item.countLength - 1;
      item.fragments = allocationFragmentsFor(item.countStart, item.countLength, sectionStartCount);
      item.startRow = item.fragments[0]?.row || 1;
      item.endRow = item.fragments.at(-1)?.row || item.startRow;
      item.displayRowSpan = item.fragments.length;
      nextAllocationCount = item.countEnd + 1;
    });
    section.allocatedRows = Math.ceil(section.allocatedCountLength / 8);
    section.provisionalRows = section.allocations.filter(({ provisional }) => provisional).reduce((total, item) => total + item.rowSpan, 0);
    section.provisionalCountLength = section.allocations.filter(({ provisional }) => provisional).reduce((total, item) => total + item.countLength, 0);
    section.state = section.allocatedCountLength === 32
      ? (section.provisionalRows > 0 ? 'provisional' : 'explicit')
      : section.allocatedCountLength < 32 ? 'incomplete' : 'overflow';
    // Four rows are an optional visual reference frame. They never affect
    // section duration or the start count of the following source section.
    section.rows = Math.max(4, Math.ceil(section.allocatedCountLength / 8));
    section.endCount = section.allocatedCountLength > 0
      ? sectionStartCount + section.allocatedCountLength - 1
      : sectionStartCount - 1;
    if (pendingFormations.length > 0) {
      const sectionEndCount = section.endCount;
      const accepted = pendingFormations.filter((formation) => formation.count >= sectionStartCount && formation.count <= sectionEndCount);
      if (accepted.length > 0) {
        section.formations = accepted.map(({ count, positions }) => ({ count, positions }));
        section.formationSourceLines = accepted.map(({ sourceLine }) => sourceLine);
      }
    }
    sections.push(section);
    pendingFormations = [];
    // Variation blocks are per-section: a section boundary closes any open
    // block and retires both base runs (D015 §3.2: blocks replace base inside
    // their own section only).
    variantState = null;
    baseRun = [];
    releasedRun = null;
    sectionStartCount += section.allocatedCountLength;
    section = null;
    sequence = null;
    allocation = null;
  };

  String(notes || '').split(/\r?\n/).forEach((raw, index) => {
    const text = raw.trim();
    const line = index + 1;
    if (!text) return;

    const bareSection = text === '#';
    const sectionHeading = text.match(/^#(?!#)\s*(.+)$/);
    if (bareSection || sectionHeading) {
      finishSection();
      const writtenLabel = sectionHeading?.[1]?.trim();
      const label = bareSection
        ? `Section ${sections.length + 1}`
        : /^[a-z]$/i.test(writtenLabel)
          ? `Section ${writtenLabel.toUpperCase()}`
          : writtenLabel;
      section = { id: `canvas-section-${sections.length + 1}`, label, sourceLine: line, allocations: [], noteLines: [], provisionalLabel: bareSection };
      return;
    }

    const sequenceHeading = text.match(/^##(?!#)\s*(.+)$/);
    if (sequenceHeading && section) {
      finishAllocation();
      sequence = { title: sequenceHeading[1].trim(), sourceLine: line };
      return;
    }

    // MCRO001-D002 §5.9 source comments: a line beginning `//` is an author
    // note (a teaching note when the author converts it deliberately), never
    // movement. It consumes no counts and never becomes a provisional
    // allocation row; the exact source text is retained with its section.
    const teachingNote = text.match(/^\/\/\s?(.*)$/);
    if (teachingNote) {
      if (section) section.noteLines.push({ text: teachingNote[1].trim(), sourceLine: line });
      return;
    }

    // MCRO001-F0065 (T-MCRO-065): accepted @group lines are consumed whole —
    // they bind cast numbers, never a movement row and never counts. Groups
    // are sheet-level, so they are accepted before the first section too
    // (the D015 §3.1 example puts them in the preamble). A rejected @group
    // line — malformed name, bad or duplicate numbers, duplicate group name —
    // falls through to the ordinary handling: a visible provisional row
    // inside a section; invisible preamble text, the existing convention.
    if (/^@group\s/i.test(text)) {
      const group = canvasGroupFor(text);
      if (group && !groupNames.has(group.name)) {
        groups.push(group);
        groupNames.add(group.name);
        groupTokenLines.push({ sourceLine: line, kind: 'group', name: group.name });
        return;
      }
    }

    // MCRO001-F0065 (T-MCRO-065): accepted @variant lines open a block whose
    // allocation lines inherit the named base run — the trailing contiguous
    // base run, or the previous block's run when blocks follow one another
    // directly (A then B over the same base span). The variant group must
    // already exist and a block needs a section and a base run to replace;
    // anything else rejects the line, which falls through to the ordinary
    // handling like a rejected @group line.
    if (section && /^@variant\s/i.test(text)) {
      const variant = canvasVariantFor(text);
      const named = baseRun.length > 0 ? baseRun : releasedRun;
      if (variant && groupNames.has(variant.name) && named && named.length > 0) {
        const queue = [];
        named.forEach(({ startRow, endRow }) => {
          for (let row = startRow; row <= endRow; row += 1) queue.push({ row });
        });
        variantState = { name: variant.name, queue, named };
        groupTokenLines.push({ sourceLine: line, kind: 'variant', name: variant.name });
        // Unlike @formation (garnish around an open allocation), @variant is
        // a structural boundary: the open base allocation closes here so
        // prose written after the token starts a NEW row that the block owns.
        finishAllocation();
        sequence = null;
        return;
      }
    }

    // MCRO001-F0064 (T-MCRO-064): an accepted `@formation` line is consumed
    // whole (never a movement row, never a count-consuming allocation) and
    // attaches to the open section. Counts must be strictly increasing against
    // the section's ACCEPTED formations; a rejected line — malformed, duplicate
    // or non-increasing — falls through to the ordinary movement handling
    // below and stays visible as a provisional row, leaving the accepted
    // count sequence untouched. Like `//` notes, formations do not close the
    // open allocation: movement prose continues around them.
    if (section && /^@formation\s/i.test(text)) {
      const formation = canvasFormationFor(text);
      if (formation
        && (pendingFormations.length === 0 || formation.count > pendingFormations[pendingFormations.length - 1].count)) {
        pendingFormations.push({ ...formation, sourceLine: line });
        return;
      }
    }

    const parsedAllocation = canvasAllocationFor(text);
    if (parsedAllocation && section) {
      finishAllocation();
      const countStart = sectionStartCount + section.allocations.reduce((total, item) => total + item.countLength, 0);
      const fragments = allocationFragmentsFor(countStart, parsedAllocation.countLength, sectionStartCount);
      const startRow = fragments[0]?.row || 1;
      const displayRowSpan = fragments.length;
      allocation = {
        id: `${section.id}-allocation-${section.allocations.length + 1}`,
        ...parsedAllocation,
        countStart,
        countEnd: countStart + parsedAllocation.countLength - 1,
        fragments,
        startRow,
        endRow: fragments.at(-1)?.row || startRow,
        displayRowSpan,
        sourceLine: line,
        sequenceTitle: sequence?.title || null,
        sequenceSourceLine: sequence?.sourceLine || null,
        movementLines: parsedAllocation.movement ? [{ text: parsedAllocation.movement, sourceLine: line }] : [],
        ...variantMarkFor(startRow, displayRowSpan),
      };
      section.allocations.push(allocation);
      sequence = null;
      return;
    }

    if (allocation && section && !allocation.provisional) {
      allocation.movementLines.push({ text, sourceLine: line });
      return;
    }

    // Natural movement lines are never discarded just because the writer has
    // not added timing syntax yet. Give each one a visibly provisional row;
    // `[8]` / `[n×8]` remains the way to confirm that allocation explicitly.
    if (section) {
      finishAllocation();
      const countStart = sectionStartCount + section.allocations.reduce((total, item) => total + item.countLength, 0);
      const fragments = allocationFragmentsFor(countStart, 8, sectionStartCount);
      const startRow = fragments[0]?.row || 1;
      allocation = {
        id: `${section.id}-allocation-${section.allocations.length + 1}`,
        rowSpan: 1,
        countLength: 8,
        durationLabel: '[8?]',
        countStart,
        countEnd: countStart + 7,
        fragments,
        startRow,
        endRow: fragments.at(-1)?.row || startRow,
        displayRowSpan: fragments.length,
        sourceLine: line,
        sequenceTitle: sequence?.title || null,
        sequenceSourceLine: sequence?.sourceLine || null,
        movementLines: [{ text: stripBullet(text), sourceLine: line }],
        provisional: true,
        ...variantMarkFor(startRow, fragments.length),
      };
      section.allocations.push(allocation);
      sequence = null;
    }
  });

  finishSection();
  return {
    schemaVersion: 1,
    sections,
    // MCRO001-F0065 (T-MCRO-065): parity — a source with no accepted group or
    // variant tokens projects EXACTLY the pre-F0065 shape; both keys attach
    // only when at least one @group line was accepted.
    ...(groups.length > 0 ? { groups, groupTokenLines } : {}),
    sourceText: String(notes || ''),
  };
}

function sectionLabelForPrevious(heading) {
  return `${titleCase(heading.kind)} ${heading.number - 1}`;
}

function addGroup(groups, heading, sourceLine, options = {}) {
  const explicit = heading.type === 'explicit-count';
  const rejectedCount = heading.type === 'invalid-explicit-count';
  const group = {
    label: heading.label,
    countLength: explicit ? heading.countLength : DEFAULT_SECTION_COUNTS,
    countSource: explicit ? 'explicit' : rejectedCount ? 'unresolved-out-of-range' : 'default-4x8',
    confidence: explicit ? 0.98 : heading.type === 'numbered' ? 0.82 : rejectedCount ? 0.4 : 0.72,
    provisional: !explicit,
    inferred: Boolean(options.inferred),
    sourceLine,
    notes: options.notes ? [...options.notes] : [],
    explicitBars: [],
    ...(rejectedCount ? { rejectedCountExpression: heading.rejectedCountExpression } : {}),
  };
  if (group.inferred) group.confidence = 0.58;
  groups.push(group);
  return group;
}

function barLengthsFor(group) {
  if (group.explicitBars.length > 0) return group.explicitBars.map(({ countLength }) => countLength);
  return Array.from({ length: Math.ceil(group.countLength / 8) }, (_, index) => Math.min(8, group.countLength - index * 8));
}

function buildSnapshot(groups) {
  const sections = [];
  const blocks = [];
  let nextCount = 1;
  groups.forEach((group, sectionIndex) => {
    const sectionId = `section-${sectionIndex + 1}`;
    sections.push({
      id: sectionId,
      label: group.label,
      kind: 'custom',
      startCount: nextCount,
      endCount: nextCount + group.countLength - 1,
      ...(group.notes.length > 0 ? { practiceNote: group.notes.join('\n') } : {}),
    });
    const barLengths = barLengthsFor(group);
    let blockStart = nextCount;
    barLengths.forEach((countLength) => {
      blocks.push({
        id: `block-${blocks.length + 1}`,
        label: String(blocks.length + 1),
        startCount: blockStart,
        countLength,
        sectionId,
      });
      blockStart += countLength;
    });
    nextCount += group.countLength;
  });
  return { schemaVersion: 1, sections, blocks, scenes: [], cues: [] };
}

function formLetterFor(label) {
  const value = String(label || '').trim();
  const melody = value.match(/\bmelody\s+([A-Z])\b/i);
  if (melody) return melody[1].toUpperCase();
  const section = value.match(/^section\s+([A-Z])(?:\d+)?$/i);
  if (section) return section[1].toUpperCase();
  const bare = value.match(/^([A-Z])(?:\d+)?$/i);
  return bare ? bare[1].toUpperCase() : null;
}

function songRoleFor(label) {
  const normalised = String(label || '').trim().toLowerCase().replace(/\s+\d+$/, '');
  const roles = {
    intro: 'Intro', verse: 'Verse', 'pre-chorus': 'Pre-chorus', chorus: 'Chorus / refrain',
    refrain: 'Chorus / refrain', bridge: 'Bridge', break: 'Break', instrumental: 'Instrumental',
    solo: 'Solo', 'shout chorus': 'Shout chorus', outro: 'Outro',
  };
  return roles[normalised] || null;
}

/**
 * Project absolute dancer counts into the standard 4/4 swing hierarchy without
 * confusing A/B form identity with pop-style Verse/Chorus song roles.
 */
export function describeDancerMusicStructure(sections = [], metadataSections = []) {
  const describedSections = sections.map((section, index) => {
    const metadata = metadataSections[index] || {};
    const countLength = Math.max(0, Number(section.endCount) - Number(section.startCount) + 1);
    const eightCountBlocks = countLength % 8 === 0 ? countLength / 8 : null;
    const musicalBars = countLength % 4 === 0 ? countLength / 4 : null;
    return {
      sectionId: section.id || metadata.sectionId || `section-${index + 1}`,
      label: section.label || metadata.label || `Part ${index + 1}`,
      sourceLine: metadata.sourceLine,
      countLength,
      eightCountBlocks,
      musicalBars,
      isEightBarFormSection: countLength === 32,
      formLetter: formLetterFor(section.label || metadata.label),
      songRole: songRoleFor(section.label || metadata.label),
      provisional: Boolean(metadata.provisional),
    };
  });

  const formCycles = [];
  let runStart = 0;
  while (runStart < describedSections.length) {
    if (!describedSections[runStart].isEightBarFormSection) { runStart += 1; continue; }
    let runEnd = runStart;
    while (runEnd < describedSections.length && describedSections[runEnd].isEightBarFormSection) runEnd += 1;
    for (let index = runStart; index + 3 < runEnd; index += 4) {
      const members = describedSections.slice(index, index + 4);
      const hasCompletePattern = members.every(({ formLetter }) => Boolean(formLetter));
      const pattern = hasCompletePattern ? members.map(({ formLetter }) => formLetter).join('') : null;
      const recognisedPattern = Boolean(pattern && RECOGNISED_32_BAR_FORMS.has(pattern));
      formCycles.push({
        id: `form-cycle-${formCycles.length + 1}`,
        label: recognisedPattern ? `${pattern} form cycle` : '32-bar form candidate',
        pattern,
        recognisedPattern,
        startCount: Number(sections[index].startCount),
        endCount: Number(sections[index + 3].endCount),
        counts: 128,
        musicalBars: 32,
        eightCountBlocks: 16,
        certainty: members.some(({ provisional }) => provisional) ? 'provisional' : 'derived',
        sectionIds: members.map(({ sectionId }) => sectionId),
      });
    }
    runStart = runEnd;
  }

  const totalCounts = describedSections.reduce((total, section) => total + section.countLength, 0);
  return {
    meterAssumption: '4/4',
    totalCounts,
    fullEightCountBlocks: Math.floor(totalCounts / 8),
    trailingCounts: totalCounts % 8,
    musicalBars: totalCounts % 4 === 0 ? totalCounts / 4 : null,
    sections: describedSections,
    formCycles,
    songRoles: describedSections.filter(({ songRole }) => songRole),
  };
}

/**
 * Parse free-writing into an API-safe snapshot plus UI-facing interpretation
 * metadata. Defaults are deliberately visible in `analysis`: the page can ask
 * for confirmation without making Edyta complete a form before she can write.
 */
export function analyseBlueprintNotes(notes) {
  const lines = normaliseLines(notes);
  const groups = [];
  const unresolved = [];
  const preamble = [];
  let current = null;
  let sawHeading = false;

  lines.forEach((entry) => {
    // D002 §5.9: `// …` author note/teaching-note lines are never headings or
    // timing; keep them with the surrounding part as source notes.
    if (/^\/\//.test(entry.text)) {
      if (current) current.notes.push(entry.text.replace(/^\/\/\s?/, ''));
      else preamble.push(entry);
      return;
    }
    const explicitBar = explicitBarFor(entry.text);
    if (explicitBar) {
      if (!current) {
        current = addGroup(groups, { type: 'named', label: 'Draft' }, entry.line);
        sawHeading = true;
      }
      const requestedLength = explicitBar.countLength * explicitBar.repeat;
      const currentExplicitLength = current.explicitBars.reduce((total, bar) => total + bar.countLength, 0);
      if (currentExplicitLength + requestedLength <= MAX_SECTION_COUNTS) {
        current.notes.push(entry.text);
        for (let index = 0; index < explicitBar.repeat; index += 1) {
          current.explicitBars.push({ countLength: explicitBar.countLength, sourceLine: entry.line, label: explicitBar.label });
        }
        current.countLength = currentExplicitLength + requestedLength;
        current.countSource = 'explicit-bars';
        current.confidence = 0.99;
        current.provisional = false;
      } else {
        current.notes.push(entry.text);
        unresolved.push({
          code: 'BAR_TOTAL_OUT_OF_RANGE',
          severity: 'review',
          sectionId: `section-${groups.length}`,
          line: entry.line,
          message: `${current.label}'s explicit bars exceed ${MAX_SECTION_COUNTS} counts and need review.`,
        });
      }
      return;
    }
    const heading = entry.bareSection
      ? { type: 'bare-section', label: `Section ${groups.length + 1}` }
      : headingFor(entry.text);
    if (!heading) {
      if (current) current.notes.push(entry.text);
      else preamble.push(entry);
      return;
    }

    if (!sawHeading && preamble.length > 0) {
      if (heading.type === 'numbered' && heading.number > 1) {
        const inferredHeading = {
          type: 'numbered',
          kind: heading.kind,
          number: heading.number - 1,
          label: sectionLabelForPrevious(heading),
        };
        const inferred = addGroup(groups, inferredHeading, preamble[0].line, {
          inferred: true,
          notes: preamble.map(({ text }) => text),
        });
        unresolved.push({
          code: 'INFERRED_LEADING_SECTION',
          severity: 'review',
          sectionId: `section-${groups.length}`,
          line: preamble[0].line,
          message: `${inferred.label} was inferred from the movement written before ${heading.label}.`,
        });
      } else {
        unresolved.push({
          code: 'CONTEXT_BEFORE_FIRST_SECTION',
          severity: 'info',
          line: preamble[0].line,
          message: 'Text before the first heading was kept as page context, not counted as movement.',
        });
      }
    }

    current = addGroup(groups, heading, entry.line);
    sawHeading = true;
    if (heading.type === 'invalid-explicit-count') {
      unresolved.push({
        code: 'COUNT_LENGTH_OUT_OF_RANGE',
        severity: 'review',
        sectionId: `section-${groups.length}`,
        line: entry.line,
        message: `${heading.label}'s requested ${heading.rejectedCountExpression} counts was not used; enter a value from 1 to ${MAX_SECTION_COUNTS}.`,
      });
    }
  });

  if (!sawHeading && preamble.length > 0) {
    current = addGroup(groups, { type: 'named', label: 'Draft' }, preamble[0].line, {
      notes: preamble.map(({ text }) => text),
    });
    current.confidence = 0.35;
    unresolved.push({
      code: 'NO_STRUCTURE_HEADING',
      severity: 'review',
      sectionId: 'section-1',
      line: preamble[0].line,
      message: 'Add a phrase or section heading when you know where this movement belongs.',
    });
  }

  groups.forEach((group, index) => {
    if (!group.provisional) return;
    unresolved.push({
      code: 'COUNT_LENGTH_PROVISIONAL',
      severity: 'review',
      sectionId: `section-${index + 1}`,
      line: group.sourceLine,
      message: `${group.label} is provisionally 4 x 8 counts until its timing is confirmed.`,
    });
  });

  const snapshot = buildSnapshot(groups);
  const averageConfidence = groups.length
    ? groups.reduce((total, group) => total + group.confidence, 0) / groups.length
    : 0;
  const explicitCountSections = groups.filter(({ countSource }) => countSource === 'explicit' || countSource === 'explicit-bars').length;
  const analysisSections = groups.map((group, index) => ({
    sectionId: `section-${index + 1}`,
    label: group.label,
    sourceLine: group.sourceLine,
    countLength: group.countLength,
    countSource: group.countSource,
    confidence: group.confidence,
    provisional: group.provisional,
    inferred: group.inferred,
    movementLines: group.notes.length,
    barLengths: group.countSource === 'explicit' || group.countSource === 'explicit-bars' ? barLengthsFor(group) : [],
    ...(group.rejectedCountExpression ? { rejectedCountExpression: group.rejectedCountExpression } : {}),
  }));

  return {
    snapshot,
    analysis: {
      mode: groups.length === 0 ? 'empty' : !sawHeading ? 'free-writing' : explicitCountSections === groups.length ? 'count-map' : 'structured-notes',
      confidence: Number(averageConfidence.toFixed(2)),
      provisional: groups.some(({ provisional }) => provisional),
      sectionsDetected: groups.length,
      explicitCountSections,
      totalCounts: snapshot.sections.at(-1)?.endCount || 0,
      preamble: sawHeading ? preamble.map(({ text }) => text) : [],
      unresolved,
      sections: analysisSections,
      musicStructure: describeDancerMusicStructure(snapshot.sections, analysisSections),
    },
  };
}

export function blueprintFromNotes(notes) {
  return analyseBlueprintNotes(notes).snapshot;
}
